import { pipe } from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as TE from "fp-ts/TaskEither"
import * as T from "fp-ts/Task"
import { getService } from "@hoppscotch/common/modules/dioc"
import { OrganizationPlatformDef } from "@hoppscotch/common/platform/organization"
import { platform } from "@hoppscotch/common/platform"
import { markRaw, ref } from "vue"
import { getKernelMode } from "@hoppscotch/kernel"
import { diag } from "@hoppscotch/common/kernel/log"

import {
  isDefaultCloudInstance,
  getEffectiveHostname,
} from "@app/helpers/utils/cloud-for-orgs"
import { OrganizationService } from "@app/services/organization.service"
import OrganizationSwitcherDropdown from "@app/components/organization/SwitcherDropdown.vue"
import { ROOT_DOMAIN_PATTERN } from "@app/helpers/validation/domain-patterns"
import { VENDORED_INSTANCE_CONFIG } from "@hoppscotch/common/platform/instance"

const CLOUD_ROOT_DOMAIN = "hoppscotch.io"

// Prevents race conditions when rapidly clicking between orgs. Without this lock,
// multiple webviews could get created simultaneously which would leave orphaned windows
// around since the close logic wouldn't be able to track them properly. The lock gets
// checked at the start of switchToInstanceDesktop and released at the end, so any
// clicks during the transition get ignored.
const isSwitching = ref(false)

// resolves the root domain for the current hostname, used to construct org
// subdomain URLs (e.g. acme.hoppscotch.io) and admin panel links. on web
// window.location.hostname gives us the real domain directly, on desktop
// getEffectiveHostname() normalizes the app:// underscored hostname back
// to dots (acme_hoppscotch_io -> acme.hoppscotch.io) so we can match
// against the same patterns
//
// hostname resolution table (effective hostname -> root domain):
//
//   web:
//     hoppscotch.io                              -> hoppscotch.io
//     acme.hoppscotch.io (prod org)              -> hoppscotch.io
//     localhost                                  -> VITE_ORG_ROOT_DOMAIN or hoppscotch.io
//     cloud-staging.deploychan.hoppscotch.com    -> cloud-staging.deploychan.hoppscotch.com
//     acme.cloud-staging.deploychan...           -> cloud-staging.deploychan.hoppscotch.com
//     cloud-orgs-staging.deploychan...           -> cloud-orgs-staging.deploychan.hoppscotch.com
//     acme.cloud-orgs-staging.deploychan...      -> cloud-orgs-staging.deploychan.hoppscotch.com
//     unknown (a.b.c)                            -> b.c (strips first label)
//     unknown (a.b)                              -> a.b (returns as-is)
//
//   desktop (macOS/Linux), custom protocol app://<host>/:
//     app://hoppscotch/          -> hostname "hoppscotch"              -> hoppscotch.io
//     app://hoppscotch_io/       -> hostname "hoppscotch_io"          -> hoppscotch.io  (after _ -> . normalization)
//     app://acme_hoppscotch_io/  -> hostname "acme_hoppscotch_io"     -> hoppscotch.io  (after _ -> . normalization)
//
//   desktop (Windows), custom protocol http://app.<host>/:
//     WebView2 rewrites app:// to http:// with the scheme prepended as a
//     subdomain, so app://hoppscotch_io/ becomes http://app.hoppscotch_io/
//     (see WHITELISTED_ORIGINS in .env.example for the per-platform format).
//     getEffectiveHostname() strips the "app." prefix before underscore
//     replacement so the downstream resolution is identical to macOS/Linux:
//     http://app.hoppscotch/              -> "app.hoppscotch"              -> "hoppscotch"              -> hoppscotch.io
//     http://app.hoppscotch_io/           -> "app.hoppscotch_io"           -> "hoppscotch_io"           -> hoppscotch.io
//     http://app.acme_hoppscotch_io/      -> "app.acme_hoppscotch_io"      -> "acme_hoppscotch_io"      -> hoppscotch.io
//
// the hoppscotch.io wildcard catches both the root domain itself and any
// org subdomain under it (acme.hoppscotch.io), as well as the vendored
// desktop hostname ("hoppscotch") which doesn't have dots, staging domains
// are checked explicitly since they have different root domains and the
// fallback extracts the root domain dynamically from the hostname parts
// rather than assuming any particular staging URL
const getRootDomain = (): string => {
  const hostname = getEffectiveHostname()

  // hoppscotch.io itself, the vendored desktop hostname ("hoppscotch"), or
  // any org subdomain like acme.hoppscotch.io
  if (
    hostname === "hoppscotch.io" ||
    hostname === "hoppscotch" ||
    hostname.endsWith(".hoppscotch.io")
  ) {
    return CLOUD_ROOT_DOMAIN
  }

  // localhost with optional env var override for testing against custom
  // deployments, validated against ROOT_DOMAIN_PATTERN to prevent invalid
  // domains from breaking org URL construction
  if (hostname === "localhost") {
    return pipe(
      O.fromNullable(import.meta.env.VITE_ORG_ROOT_DOMAIN),
      O.filter((domain) => ROOT_DOMAIN_PATTERN.test(domain)),
      O.getOrElse(() => CLOUD_ROOT_DOMAIN)
    )
  }

  // cloud staging, handles both the root staging domain and org subdomains
  // under it (e.g. acme.cloud-staging.deploychan.hoppscotch.com)
  if (
    hostname === "cloud-staging.deploychan.hoppscotch.com" ||
    hostname.endsWith(".cloud-staging.deploychan.hoppscotch.com")
  ) {
    return "cloud-staging.deploychan.hoppscotch.com"
  }

  // cloud orgs staging, same structure but separate staging environment for
  // the org-specific deployment
  if (
    hostname === "cloud-orgs-staging.deploychan.hoppscotch.com" ||
    hostname.endsWith(".cloud-orgs-staging.deploychan.hoppscotch.com")
  ) {
    return "cloud-orgs-staging.deploychan.hoppscotch.com"
  }

  // unrecognized hostname, extract the root domain dynamically by stripping
  // the first subdomain label instead of silently falling through to a
  // hardcoded staging URL for hostnames we didn't anticipate
  const parts = hostname.split(".")
  return parts.length > 2 ? parts.slice(1).join(".") : hostname
}

// Builds the full redirect URL for org switching on web. Takes an optional org domain
// and constructs either https://hoppscotch.io (for null) or https://acme.hoppscotch.io
// (for "acme"). Protocol is preserved from current window location.
const buildOrgUrl = (orgDomain: string | null): string => {
  const rootDomain = getRootDomain()
  const protocol = window.location.protocol

  return pipe(
    O.fromNullable(orgDomain),
    O.fold(
      () => `${protocol}//${rootDomain}`,
      (domain) => `${protocol}//${domain}.${rootDomain}`
    )
  )
}

// builds the host parameter for the appload plugin on desktop. the Rust side
// uses this as a URL query param (?org=acme.hoppscotch.io) rather than
// changing the webview hostname. all webviews share the same app://hoppscotch
// origin so Tauri IPC works. the query param carries the org context for the
// JS side to read (getEffectiveHostname, getOrganizationDomainDesktop) and
// for the kernel store to derive per-org file isolation
const buildOrgHost = (orgDomain: string | null): O.Option<string> =>
  pipe(
    O.fromNullable(orgDomain),
    O.fold(
      () => O.some(CLOUD_ROOT_DOMAIN),
      (domain) => O.some(`${domain}.${CLOUD_ROOT_DOMAIN}`)
    )
  )

// Web platform org switching is simpler, just redirect to the new subdomain and let the
// browser handle everything. The full page reload means all state gets reset automatically.
const switchToInstanceWeb = (orgDomain: string | null): void => {
  window.location.href = buildOrgUrl(orgDomain)
}

// Creates the instance configuration for loading an org. The "cloud-org" kind indicates
// this uses the vendored Hoppscotch bundle but with a different host context (so
// window.location.hostname maps to the org subdomain). Regular "cloud" kind is for the
// default instance. The serverUrl becomes the host param that eventually determines the
// webview URL.
const createOrgInstance = (
  orgDomain: string | null,
  host: O.Option<string>
) => ({
  kind: pipe(
    O.fromNullable(orgDomain),
    O.fold(
      () => "cloud" as const,
      () => "cloud-org" as const
    )
  ),
  serverUrl: pipe(
    host,
    O.getOrElse(() => CLOUD_ROOT_DOMAIN)
  ),
  displayName: pipe(
    O.fromNullable(orgDomain),
    O.fold(
      () => "Hoppscotch Cloud",
      (domain) => `${domain} Organization`
    )
  ),
  version: "latest",
  lastUsed: new Date().toISOString(),
  bundleName: "Hoppscotch",
})

// Attempts to load the org instance via the instance platform service, which is the
// preferred path since it handles persistence properly (saves to recent instances, updates
// last used timestamp, etc). The service wraps the appload plugin with additional logic
// around state management. Returns a TaskEither so it can be chained with the fallback
// if unavailable.
const loadViaInstancePlatform = (
  orgDomain: string | null,
  host: O.Option<string>
): TE.TaskEither<string, void> =>
  pipe(
    O.fromNullable(platform.instance?.loadInstance),
    TE.fromOption(() => "Instance platform not available"),
    TE.chain((loadInstance) =>
      TE.tryCatch(
        async () => {
          const instance = createOrgInstance(orgDomain, host)
          diag(
            "org-platform",
            `loadViaInstancePlatform: orgDomain=${orgDomain}, host=${O.toUndefined(host) ?? "(none)"}, kind=${instance.kind}`
          )
          const result = await loadInstance(instance, {
            host: O.toUndefined(host),
          })

          diag(
            "org-platform",
            `loadViaInstancePlatform result: success=${result.success}, message=${result.message}`
          )
          if (!result.success) {
            throw new Error(result.message)
          }
        },
        (error) =>
          error instanceof Error ? error.message : "Failed to load instance"
      )
    )
  )

// Direct fallback that bypasses the instance platform service and calls the appload
// plugin directly. This gets used when org switching happens before the instance service
// has fully initialized (like during app startup). The downside is it doesn't persist
// the instance to recent instances list or update last used timestamps, but it gets
// the webview loaded which matters more for user experience.
// TODO: Look into that.
const loadDirectFallback = (
  orgDomain: string | null,
  host: O.Option<string>
): TE.TaskEither<string, void> =>
  TE.tryCatch(
    async () => {
      diag(
        "org-platform",
        `loadDirectFallback: orgDomain=${orgDomain}, host=${O.toUndefined(host) ?? "(none)"}`
      )
      const { load, close } = await import("@hoppscotch/plugin-appload")
      const { getCurrentWebviewWindow } =
        await import("@tauri-apps/api/webviewWindow")

      const loadResponse = await load({
        bundleName: "Hoppscotch",
        host: O.toUndefined(host),
        window: {
          title: pipe(
            O.fromNullable(orgDomain),
            O.fold(
              () => "Hoppscotch",
              (domain) => `Hoppscotch - ${domain}`
            )
          ),
          width: 1200,
          height: 800,
          resizable: true,
        },
      })

      diag(
        "org-platform",
        `loadDirectFallback load result: success=${loadResponse.success}, label=${loadResponse.windowLabel}`
      )
      if (!loadResponse.success) {
        throw new Error("Failed to load organization instance")
      }

      // Close the current window if it's not the main window. This is typically the
      // desktop shell or previous org instance window. Main window doesn't get closed.
      const currentWindow = getCurrentWebviewWindow()
      diag(
        "org-platform",
        `loadDirectFallback: closing currentWindow=${currentWindow.label}`
      )
      if (currentWindow.label !== "main") {
        await close({ windowLabel: currentWindow.label })
      }
    },
    (error) =>
      error instanceof Error ? error.message : "Failed to load instance"
  )

// Desktop org switching creates a new webview with the correct host context, so
// window.location.hostname automatically reflects the org subdomain. This differs from
// the typical approach where state would need to be passed through, here the URL itself
// becomes the source of truth. Tries the instance platform service first, falls back to
// direct appload if the service isn't ready yet.
// The isSwitching lock prevents race conditions where multiple clicks could create
// multiple webviews.
const switchToInstanceDesktop = async (
  orgDomain: string | null
): Promise<void> => {
  if (isSwitching.value) {
    diag(
      "org-platform",
      `switchToInstanceDesktop: already switching, ignoring orgDomain=${orgDomain}`
    )
    return
  }

  isSwitching.value = true
  diag(
    "org-platform",
    `switchToInstanceDesktop: starting, orgDomain=${orgDomain}`
  )

  try {
    // When orgDomain is null (switching back to default), load the vendored
    // instance instead of creating a separate "cloud" instance. On desktop
    // the vendored instance (app://hoppscotch/) and a "cloud" instance
    // (app://hoppscotch_io/) are functionally identical since both resolve
    // isDefaultCloudInstance to true. Having two separate webviews for the
    // same default state causes "cloud" and "vendored" to appear as
    // duplicate entries in the switcher.
    if (orgDomain === null) {
      diag(
        "org-platform",
        "switchToInstanceDesktop: switching to vendored (default)"
      )

      await pipe(
        O.fromNullable(platform.instance?.loadInstance),
        TE.fromOption(() => "Instance platform not available"),
        TE.chain((loadInstance) =>
          TE.tryCatch(
            async () => {
              const result = await loadInstance(VENDORED_INSTANCE_CONFIG)
              if (!result.success) {
                throw new Error(result.message)
              }
            },
            (error) =>
              error instanceof Error
                ? error.message
                : "Failed to load vendored instance"
          )
        ),
        TE.orElse(() =>
          TE.tryCatch(
            async () => {
              const { load, close: closeWindow } =
                await import("@hoppscotch/plugin-appload")
              const { getCurrentWebviewWindow } =
                await import("@tauri-apps/api/webviewWindow")

              const loadResponse = await load({
                bundleName: "Hoppscotch",
              })

              if (!loadResponse.success) {
                throw new Error("Failed to load vendored instance")
              }

              const currentWindow = getCurrentWebviewWindow()
              if (currentWindow.label !== "main") {
                await closeWindow({ windowLabel: currentWindow.label })
              }
            },
            (error) =>
              error instanceof Error
                ? error.message
                : "Failed to load vendored instance"
          )
        ),
        TE.fold(
          (error) =>
            T.of(console.error(`[OrganizationPlatform] Error:`, error)),
          () => T.of(undefined)
        )
      )()

      return
    }

    const host = buildOrgHost(orgDomain)

    await pipe(
      loadViaInstancePlatform(orgDomain, host),
      TE.orElse(() => loadDirectFallback(orgDomain, host)),
      TE.fold(
        (error) => T.of(console.error(`[OrganizationPlatform] Error:`, error)),
        () => T.of(undefined)
      )
    )()
  } finally {
    isSwitching.value = false
  }
}

// Main entry point for org switching. Delegates to either web (redirect) or desktop
// (webview) implementation based on the current kernel mode. This gets called from the
// org dropdown when a user clicks on an org to switch to. Web is sync since it's just a
// redirect, desktop is async since it needs to load a new webview.
const switchToInstance = async (orgDomain: string | null): Promise<void> => {
  const kernelMode = getKernelMode()

  if (kernelMode === "desktop") {
    return switchToInstanceDesktop(orgDomain)
  }

  // Web platform just does a synchronous redirect, no async operations needed
  switchToInstanceWeb(orgDomain)
}

export const def: OrganizationPlatformDef = {
  isDefaultCloudInstance,

  getOrgInfo: async () => {
    const organizationService = getService(OrganizationService)
    const orgInfo = await organizationService.getOrganizationInfo()

    return pipe(
      O.fromNullable(orgInfo),
      // Check if the user is an admin of the organization (if the user is not an admin, the error will be 'organization/organization_member_not_admin')
      O.map(({ orgID, orgDomain, orgName, orgLogo, error }) => ({
        orgID,
        orgDomain,
        name: orgName,
        logo: orgLogo || null,
        isAdmin: !error,
      })),
      O.toNullable
    )
  },

  getRootDomain,

  // opens the org creation/onboarding page. on web this is a simple navigation
  // to /orgs (same origin if on default cloud, or a redirect to the root domain
  // if on an org subdomain). on desktop the webview runs on the app:// protocol
  // so it can't navigate to https:// URLs directly, instead we open the page in
  // the system browser via kernelIO.openExternalLink which is the same approach
  // used by the device login flow (Login.vue) and the admin panel badge links in
  // SwitcherDropdown.vue
  //
  // for more context on why we use the external browser approach: Tauri's
  // WebviewWindow API (from @tauri-apps/api/webviewWindow) lets you create
  // managed in-app browser windows that can load arbitrary https:// URLs while
  // still being controlled by the Tauri app. an earlier iteration explored using
  // this for flows that need to load external web pages like onboarding, admin
  // panels, and OAuth. the idea was that the auth provider page would load inside
  // a Tauri-managed webview popup, the OAuth redirect would happen within that
  // same webview, and the app would capture the token via Tauri's event system
  // or by reading the redirect URL, then close the popup. this would keep the
  // entire flow in-app without needing to bounce to the system browser and back
  //
  // this was scoped down for a few reasons: some OAuth providers actively block
  // non-browser user agents and embedded webviews (Google's OAuth policy for
  // example explicitly prohibits this), managing auth state and cookies across
  // multiple webview windows adds complexity around session isolation (which is
  // the same class of issue we found with WebKit storage leaking between
  // instances), and the redirect URI handling gets tricky since the managed
  // webview would need to intercept navigations to capture the OAuth callback.
  // the system browser approach sidesteps all of this since the browser handles
  // auth state natively and the token comes back through the local server
  // redirect (see Login.vue's device login flow with hopp_auth_port). see
  // tauri-apps/tauri#3020 and the tauri-plugin-oauth repo by FabianLars for
  // more context on the tradeoffs between managed webview vs external browser
  // approaches for desktop OAuth
  //
  // TODO: revisit the managed WebviewWindow approach, there's some prior art
  // from the earlier iteration that could be picked back up if we want to
  // bring these flows in-app in the future
  initiateOnboarding: () => {
    const rootDomain = def.getRootDomain()
    const onboardingUrl = `https://${rootDomain}/orgs`

    if (getKernelMode() === "desktop") {
      platform.kernelIO.openExternalLink({ url: onboardingUrl })
    } else if (isDefaultCloudInstance) {
      window.location.href = "/orgs"
    } else {
      window.location.href = onboardingUrl
    }
  },
  customOrganizationSwitcherComponent: markRaw(OrganizationSwitcherDropdown),
  switchToInstance,
}
